import FileBlock from 'app/_components/Editor/extensions/withUploads/FileBlock'
import { getIdFromSlateNode } from 'lib/slate'
import { useCallback } from 'react'
import { type RenderElementProps } from 'slate-react'
import Flow from './extensions/withFlow/components/Flow'
import Link from './extensions/withLinks/Link'
import Mention from './extensions/withMentions/Mention'
import Table from './extensions/withTables/Table'
import TableCell from './extensions/withTables/TableCell'
import TableRow from './extensions/withTables/TableRow'
import File from './extensions/withUploads/File'
import Image from './extensions/withUploads/Image'
import Video from './extensions/withUploads/Video'

export function useElementRenderer() {
  return useCallback((props: RenderElementProps) => <Element {...props} />, [])
}

export default function Element({
  attributes,
  children,
  element,
}: RenderElementProps) {
  switch (element.type) {
    case 'block-quote':
      return <blockquote {...attributes}>{children}</blockquote>
    case 'bulleted-list':
      return <ul {...attributes}>{children}</ul>
    case 'heading-one':
      return (
        <h1 id={getIdFromSlateNode(element)} {...attributes}>
          {children}
        </h1>
      )
    case 'heading-two':
      return (
        <h2 id={getIdFromSlateNode(element)} {...attributes}>
          {children}
        </h2>
      )
    case 'heading-three':
      return (
        <h3
          className="flex items-center"
          id={getIdFromSlateNode(element)}
          {...attributes}
        >
          {children}
        </h3>
      )
    case 'list-item':
      return <li {...attributes}>{children}</li>
    case 'numbered-list':
      return <ol {...attributes}>{children}</ol>
    case 'paragraph':
    case 'text':
      return <p {...attributes}>{children}</p>
    case 'file':
      return (
        <File attributes={attributes} element={element}>
          {children}
        </File>
      )
    case 'file-block':
      return (
        <FileBlock attributes={attributes} element={element}>
          {children}
        </FileBlock>
      )
    case 'image':
    case 'private-image':
      return (
        <Image attributes={attributes} element={element}>
          {children}
        </Image>
      )
    case 'video':
      return (
        <Video attributes={attributes} element={element}>
          {children}
        </Video>
      )
    case 'link':
      return (
        <Link attributes={attributes} element={element}>
          {children}
        </Link>
      )
    case 'mention':
      return (
        <Mention attributes={attributes} element={element}>
          {children}
        </Mention>
      )
    case 'table':
      return (
        <Table attributes={attributes} element={element}>
          {children}
        </Table>
      )
    case 'table-row':
      return (
        <TableRow attributes={attributes} element={element}>
          {children}
        </TableRow>
      )
    case 'table-cell':
      return (
        <TableCell attributes={attributes} element={element}>
          {children}
        </TableCell>
      )
    case 'flow':
      return (
        <Flow attributes={attributes} element={element}>
          {children}
        </Flow>
      )
    default:
      console.error(`Unhandled element format: ${element.type}`)
      return <p {...attributes}>{children}</p>
  }
}
